/**
 * API Credit Operations
 *
 * Handles credit balance fetching and caching
 * @module portal/modules/api/credits
 */

import { state } from '../state.js';
import { TIMEOUTS } from '../../../config/timeouts';
import type { CreditBalance } from '../../types';
import { createLogger } from '../../../shared/logger.js';
import { apiUrl, resolveGlobalConfig, type ApiConfig } from './config.js';
import { ensureAccessToken } from './auth.js';

const log = createLogger('portal:api:credits');
const DEBUG = false;

function logFailure(label: string, response: Response, body: unknown) {
  if (!DEBUG) return;
  log.warn(`${label} failed ${response.status} ${String(body).slice(0, 200)}`);
}

/**
 * Fetch credit balance from API
 */
export async function fetchCreditBalance(
  cfg?: ApiConfig | null,
  forceRefresh = false
): Promise<CreditBalance | null> {
  const now = Date.now();

  if (
    !forceRefresh &&
    state.creditBalance &&
    now - state.creditLastFetchedAt < TIMEOUTS.CREDIT_CACHE_TTL_MS
  ) {
    if (DEBUG) {
      log.debug('Using cached credit balance');
    }
    return state.creditBalance;
  }

  if (state.mode !== 'auth0') {
    if (DEBUG) {
      log.debug('Not fetching credits: mode is', state.mode);
    }
    return null;
  }

  const resolvedCfg = cfg ?? resolveGlobalConfig();

  try {
    const token = await ensureAccessToken(resolvedCfg, false);
    if (!token) {
      if (DEBUG) {
        log.warn('No access token for credit fetch');
      }
      return null;
    }

    const endpoint = apiUrl(resolvedCfg, 'user/profile');
    if (DEBUG) {
      log.debug('Fetching credit balance from', endpoint);
    }

    const response = await fetch(endpoint, {
      headers: {
        Authorization: 'Bearer ' + String(token),
      },
    });

    if (!response.ok) {
      let body = '';
      try {
        body = await response.text();
      } catch (err) {
        body = String(err);
      }
      logFailure('credit balance', response, body);
      return null;
    }

    const profile = await response.json();
    const balance: CreditBalance = {
      creditsRemaining: Number(profile?.creditsRemaining ?? 0),
      subscriptionStatus: profile?.subscriptionStatus,
      subscriptionExpiresAt: profile?.subscriptionExpiresAt,
      lastCreditRenewalAt: profile?.lastCreditRenewalAt,
      creditLedger: profile?.creditLedger,
    } as CreditBalance;

    state.creditBalance = balance;
    state.creditLastFetchedAt = now;

    if (DEBUG) {
      log.debug('Credit balance updated:', balance.creditsRemaining);
    }

    return balance;
  } catch (error) {
    log.error('Error fetching credit balance:', error);
    return null;
  }
}

/**
 * Clear the credit balance cache
 */
export function clearCreditCache() {
  state.creditBalance = null;
  state.creditLastFetchedAt = 0;
}
